<template>
  <div
    class="filters-container"
    :class="{ hidden: !showFilters }"
    @click.self="$emit('closed')"
  >
    <div class="filters-panel" role="dialog" aria-label="Filters">
      <InfoautoCustomerFinder
        @selectedCustomer="selectedInfoautoCustomer"
        v-if="showInfoautoSearch"
        @close="showInfoautoSearch = false"
        :showCreateCustomer="false"
      ></InfoautoCustomerFinder>

      <div class="sheet-handle" aria-hidden="true"></div>

      <!-- Header -->
      <div class="filters-header">
        <div class="filters-title">
          <i class="mdi mdi-filter-variant"></i>
          <h3>Filters</h3>
          <span v-if="activeCount" class="active-count">
            {{ activeCount }} selected
          </span>
        </div>
        <button
          type="button"
          class="icon-btn"
          title="Close (Esc)"
          @click="$emit('closed')"
        >
          <i class="mdi mdi-close"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="filters-body">
        <!-- Date -->
        <section class="filter-section">
          <div class="section-head">
            <h4 class="section-title">Order date</h4>
            <button
              v-if="sectionCount('date')"
              type="button"
              class="reset-btn"
              @click="resetSection('date')"
            >
              Reset
            </button>
          </div>

          <div class="chips">
            <button
              v-for="preset in datePresets"
              :key="preset.key"
              type="button"
              class="chip"
              :class="{ 'is-selected': isRangeActive(preset) }"
              :aria-pressed="isRangeActive(preset)"
              @click="setDates(preset.from, preset.to)"
            >
              {{ preset.label }}
            </button>
          </div>

          <!-- Mes + semanas del mes (1-7, 8-14, 15-21, 22-28, 29-fin) -->
          <div class="month-picker">
            <div class="month-nav">
              <button
                type="button"
                class="icon-btn"
                title="Previous month"
                @click="shiftMonth(-1)"
              >
                <i class="mdi mdi-chevron-left"></i>
              </button>
              <span class="month-label">{{ monthLabel }}</span>
              <button
                type="button"
                class="icon-btn"
                title="Next month"
                :disabled="isCurrentMonth"
                @click="shiftMonth(1)"
              >
                <i class="mdi mdi-chevron-right"></i>
              </button>
            </div>
            <div class="week-grid">
              <button
                v-for="range in monthRanges"
                :key="range.key"
                type="button"
                class="week-btn"
                :class="{ 'is-selected': isRangeActive(range) }"
                :aria-pressed="isRangeActive(range)"
                @click="setDates(range.from, range.to)"
              >
                <span class="week-name">{{ range.label }}</span>
                <span class="week-days">{{ range.days }}</span>
              </button>
            </div>
          </div>

          <div class="field-row">
            <div class="field">
              <label for="from-date" class="field-label">From</label>
              <input
                type="date"
                class="field-input"
                :class="{ invalid: dateRangeInvalid }"
                id="from-date"
                name="from-date"
                :max="localFilters.toDate || undefined"
                v-model="localFilters.fromDate"
              />
            </div>
            <div class="field">
              <label for="to-date" class="field-label">To</label>
              <input
                type="date"
                class="field-input"
                :class="{ invalid: dateRangeInvalid }"
                id="to-date"
                name="to-date"
                :min="localFilters.fromDate || undefined"
                v-model="localFilters.toDate"
              />
            </div>
          </div>
          <p v-if="dateRangeInvalid" class="field-error">
            "From" must be on or before "To".
          </p>
          <p
            v-else
            class="range-summary"
            :class="{ empty: !sectionCount('date') }"
          >
            <i class="mdi mdi-calendar-range"></i>
            {{ dateSummary }}
          </p>
        </section>

        <!-- Order -->
        <section class="filter-section">
          <div class="section-head">
            <h4 class="section-title">Order</h4>
            <button
              v-if="sectionCount('order')"
              type="button"
              class="reset-btn"
              @click="resetSection('order')"
            >
              Reset
            </button>
          </div>

          <div class="field">
            <span class="field-label">Payment</span>
            <div class="segmented">
              <button
                v-for="option in paymentOptions"
                :key="option.value"
                type="button"
                :class="{
                  'is-selected': localFilters.paidStatus === option.value
                }"
                :aria-pressed="localFilters.paidStatus === option.value"
                @click="localFilters.paidStatus = option.value"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div class="field">
            <span class="field-label">Type</span>
            <div class="segmented">
              <button
                v-for="option in orderTypeOptions"
                :key="option.value"
                type="button"
                :class="{
                  'is-selected': localFilters.creditRetail === option.value
                }"
                :aria-pressed="localFilters.creditRetail === option.value"
                @click="localFilters.creditRetail = option.value"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div class="field">
            <span class="field-label">Created by</span>
            <div class="segmented">
              <button
                v-for="option in createdByOptions"
                :key="option.value"
                type="button"
                :class="{
                  'is-selected': localFilters.createdBy === option.value
                }"
                :aria-pressed="localFilters.createdBy === option.value"
                @click="localFilters.createdBy = option.value"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div class="field">
            <label for="total-min" class="field-label">
              Total ({{ currency }})
            </label>
            <div class="field-row compact">
              <input
                type="number"
                inputmode="decimal"
                class="field-input"
                :class="{ invalid: totalRangeInvalid }"
                id="total-min"
                placeholder="Min"
                aria-label="Minimum total"
                v-model="localFilters.totalMin"
                min="0"
                step="0.01"
                @keyup.enter="apply"
              />
              <span class="range-sep">–</span>
              <input
                type="number"
                inputmode="decimal"
                class="field-input"
                :class="{ invalid: totalRangeInvalid }"
                placeholder="Max"
                aria-label="Maximum total"
                v-model="localFilters.totalMax"
                min="0"
                step="0.01"
                @keyup.enter="apply"
              />
            </div>
            <p v-if="totalRangeInvalid" class="field-error">
              "Min" must be less than or equal to "Max".
            </p>
          </div>

          <label class="switch-row">
            <span>Hide cancelled orders</span>
            <input
              type="checkbox"
              class="switch"
              id="hide-canceled"
              name="hide-canceled"
              v-model="localFilters.hideCancelled"
            />
          </label>
        </section>

        <!-- Customer & product -->
        <section class="filter-section">
          <div class="section-head">
            <h4 class="section-title">Customer & product</h4>
            <button
              v-if="sectionCount('customer')"
              type="button"
              class="reset-btn"
              @click="resetSection('customer')"
            >
              Reset
            </button>
          </div>

          <div v-if="canViewOtherSellers" class="field">
            <label for="seller" class="field-label">Seller</label>
            <select
              id="seller"
              name="seller"
              class="field-input"
              v-model="localFilters.seller"
            >
              <option value="">All sellers</option>
              <option
                v-for="seller in sellerOptions"
                :key="seller.id"
                :value="seller.email"
              >
                {{ seller.name }}
              </option>
            </select>
          </div>

          <div class="field">
            <label for="customer-infoauto-id" class="field-label">
              Infoauto customer
            </label>
            <!-- Cliente elegido con el buscador: se muestra el nombre, no solo el ID -->
            <div v-if="selectedCustomerName" class="selected-customer">
              <i class="mdi mdi-account-circle"></i>
              <div class="selected-customer-text">
                <span class="name">{{ selectedCustomerName }}</span>
                <span class="id">ID {{ localFilters.customerInfoautoId }}</span>
              </div>
              <button
                type="button"
                class="icon-btn small"
                title="Change customer"
                @click="showInfoautoSearch = true"
              >
                <i class="mdi mdi-account-edit-outline"></i>
              </button>
              <button
                type="button"
                class="icon-btn small"
                title="Remove customer"
                @click="localFilters.customerInfoautoId = ''"
              >
                <i class="mdi mdi-close"></i>
              </button>
            </div>
            <div v-else class="input-with-action">
              <input
                type="text"
                class="field-input"
                id="customer-infoauto-id"
                name="customer-infoauto-id"
                placeholder="Customer ID"
                v-model.trim="localFilters.customerInfoautoId"
                @keyup.enter="apply"
              />
              <button
                type="button"
                class="action-btn"
                title="Find customer"
                @click="showInfoautoSearch = true"
              >
                <i class="mdi mdi-account-search"></i>
                <span>Find</span>
              </button>
            </div>
          </div>

          <div class="field">
            <label for="shipping-name" class="field-label">Shipping name</label>
            <input
              type="text"
              class="field-input"
              id="shipping-name"
              name="shipping-name"
              placeholder="Recipient name"
              v-model.trim="localFilters.shippingAddressName"
              @keyup.enter="apply"
            />
          </div>

          <div class="field">
            <label for="nkr-code" class="field-label">Product (NKR code)</label>
            <input
              type="text"
              class="field-input"
              id="nkr-code"
              name="nkr-code"
              placeholder="e.g. 100-0078"
              v-model.trim="localFilters.nkrCode"
              @keyup.enter="apply"
            />
          </div>
        </section>
      </div>

      <!-- Footer -->
      <div class="filters-footer">
        <button
          type="button"
          class="btn-secondary"
          :disabled="!activeCount"
          @click="clear"
        >
          <i class="mdi mdi-filter-remove-outline"></i>
          Clear all
        </button>
        <button
          type="button"
          class="btn-primary"
          :disabled="!canApply"
          @click="apply"
        >
          <i class="mdi mdi-check"></i>
          Apply filters
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import InfoautoCustomerFinder from "../customer/InfoautoCustomerFinder.vue";

const DAY_MS = 24 * 60 * 60 * 1000;

// Campos de cada seccion; los pares (fechas, total min/max) cuentan como un
// solo filtro. Todos vuelven a "" salvo hideCancelled, que por defecto es true.
const SECTIONS = {
  date: [["fromDate", "toDate"]],
  order: [
    "paidStatus",
    "creditRetail",
    "createdBy",
    ["totalMin", "totalMax"],
    "hideCancelled"
  ],
  customer: ["seller", "customerInfoautoId", "shippingAddressName", "nkrCode"]
};
const FIELD_DEFAULTS = { hideCancelled: true };

// El panel se desmonta al cerrarse: se recuerda el ultimo cliente elegido con
// el buscador para seguir mostrando su nombre al volver a abrirlo.
let lastSelectedCustomer = { id: "", name: "" };

function isFieldDefault(filters, key) {
  if (key in FIELD_DEFAULTS) return !!filters[key] === FIELD_DEFAULTS[key];
  const value = filters[key];
  return value === "" || value === null || value === undefined;
}

// Fecha local en YYYY-MM-DD: toISOString() usa UTC y de noche adelanta un dia.
function toDateString(date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

// new Date("YYYY-MM-DD") se interpreta en UTC; se arma en hora local.
function parseDateString(value) {
  const [year, month, day] = String(value || "")
    .split("-")
    .map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function formatDate(date, withYear = true) {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {})
  });
}

function monthKey(date) {
  return toDateString(date).slice(0, 7);
}

function daysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toDateString(date);
}

export default {
  name: "OrdersFilters",
  props: {
    value: {
      type: Object,
      required: true
    },
    showFilters: {
      type: Boolean,
      default: false
    }
  },
  components: {
    InfoautoCustomerFinder
  },
  data() {
    return {
      selectedCustomer: { ...lastSelectedCustomer },
      showInfoautoSearch: false,
      localFilters: JSON.parse(JSON.stringify(this.value)),
      // YYYY-MM que muestra el selector; arranca en el mes del filtro aplicado
      month: (this.value.fromDate || "").slice(0, 7) || monthKey(new Date()),
      paymentOptions: [
        { value: "", label: "All" },
        { value: "1", label: "Paid" },
        { value: "0", label: "Pending" }
      ],
      orderTypeOptions: [
        { value: "", label: "All" },
        { value: "0", label: "Credit" },
        { value: "1", label: "Retail" }
      ],
      createdByOptions: [
        { value: "", label: "All" },
        { value: "user", label: "User" },
        { value: "seller", label: "Seller" }
      ]
    };
  },
  computed: {
    selectedCustomerName() {
      const { id, name } = this.selectedCustomer;
      return name && String(id) === String(this.localFilters.customerInfoautoId)
        ? name
        : "";
    },
    currency() {
      const settings = this.$store.getters["general/getAdminSettings"];
      return settings?.get?.("currency")?.value || "$";
    },
    sellers() {
      return this.$store.getters["general/getSellers"];
    },
    // Sin el permiso el API sólo devuelve las órdenes del propio vendedor.
    canViewOtherSellers() {
      return this.$store.getters["auth/userHasPermission"](
        "can_view_orders_other_sellers"
      );
    },
    // Sin email el valor seria "" y elegirlos equivaldria a "All sellers".
    sellerOptions() {
      return (this.sellers || []).filter(seller => seller.email);
    },
    dateRangeInvalid() {
      const { fromDate, toDate } = this.localFilters;
      return !!(fromDate && toDate && fromDate > toDate);
    },
    totalRangeInvalid() {
      const { totalMin, totalMax } = this.localFilters;
      if (isFieldDefault(this.localFilters, "totalMin")) return false;
      if (isFieldDefault(this.localFilters, "totalMax")) return false;
      return Number(totalMin) > Number(totalMax);
    },
    canApply() {
      return !this.dateRangeInvalid && !this.totalRangeInvalid;
    },
    // Rango en palabras: los inputs date muestran el formato del navegador y
    // no dicen cuantos dias abarca.
    dateSummary() {
      const from = parseDateString(this.localFilters.fromDate);
      const to = parseDateString(this.localFilters.toDate);
      if (from && to) {
        const days = Math.round((to - from) / DAY_MS) + 1;
        const range =
          days === 1
            ? formatDate(to)
            : `${formatDate(
                from,
                from.getFullYear() !== to.getFullYear()
              )} – ${formatDate(to)}`;
        return `${range} · ${days} ${days === 1 ? "day" : "days"}`;
      }
      if (from) return `From ${formatDate(from)} onward`;
      if (to) return `Up to ${formatDate(to)}`;
      return "Any date";
    },
    datePresets() {
      return [
        { key: "today", label: "Today", from: daysAgo(0), to: daysAgo(0) },
        {
          key: "yesterday",
          label: "Yesterday",
          from: daysAgo(1),
          to: daysAgo(1)
        },
        { key: "7d", label: "Last 7 days", from: daysAgo(6), to: daysAgo(0) },
        {
          key: "30d",
          label: "Last 30 days",
          from: daysAgo(29),
          to: daysAgo(0)
        }
      ];
    },
    monthLabel() {
      const [year, month] = this.month.split("-").map(Number);
      return new Date(year, month - 1, 1).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric"
      });
    },
    isCurrentMonth() {
      return this.month >= monthKey(new Date());
    },
    // Mes completo + semanas por dia del mes, no por calendario (lunes a
    // domingo): asi la semana 1 siempre es 1-7 y se comparan meses entre si.
    monthRanges() {
      const [year, month] = this.month.split("-").map(Number);
      const lastDay = new Date(year, month, 0).getDate();
      const day = d => `${this.month}-${String(d).padStart(2, "0")}`;
      const ranges = [
        {
          key: "month",
          label: "Whole month",
          days: `1–${lastDay}`,
          from: day(1),
          to: day(lastDay)
        }
      ];
      for (let start = 1, week = 1; start <= lastDay; start += 7, week++) {
        const end = Math.min(start + 6, lastDay);
        ranges.push({
          key: `week${week}`,
          label: `Week ${week}`,
          days: `${start}–${end}`,
          from: day(start),
          to: day(end)
        });
      }
      return ranges;
    },
    activeCount() {
      return Object.keys(SECTIONS).reduce(
        (total, section) => total + this.sectionCount(section),
        0
      );
    }
  },
  watch: {
    // Fechas escritas a mano o con un preset dentro de un solo mes: el selector
    // salta a ese mes para que se vea la semana marcada.
    "localFilters.fromDate": "syncMonthWithDates",
    "localFilters.toDate": "syncMonthWithDates",
    localFilters: {
      handler(value) {
        this.$emit("input", value);
      },
      deep: true
    }
  },
  mounted() {
    document.addEventListener("keydown", this.onKeydown);
    // En movil el panel es una hoja sobre la pagina: que no se desplace detras
    if (window.matchMedia("(max-width: 767px)").matches) {
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
    if (this.previousBodyOverflow !== undefined) {
      document.body.style.overflow = this.previousBodyOverflow;
    }
  },
  methods: {
    selectedInfoautoCustomer(customer) {
      this.localFilters.customerInfoautoId = customer.personaId;
      this.selectedCustomer = {
        id: customer.personaId,
        name: customer.nombreComp
      };
      lastSelectedCustomer = { ...this.selectedCustomer };
      this.showInfoautoSearch = false;
    },
    sectionCount(section) {
      return SECTIONS[section].filter(group =>
        [].concat(group).some(key => !isFieldDefault(this.localFilters, key))
      ).length;
    },
    resetSection(section) {
      SECTIONS[section].flat().forEach(key => {
        this.localFilters[key] =
          key in FIELD_DEFAULTS ? FIELD_DEFAULTS[key] : "";
      });
    },
    syncMonthWithDates() {
      const { fromDate, toDate } = this.localFilters;
      if (fromDate && toDate && fromDate.slice(0, 7) === toDate.slice(0, 7)) {
        this.month = fromDate.slice(0, 7);
      }
    },
    setDates(from, to) {
      this.localFilters.fromDate = from;
      this.localFilters.toDate = to;
    },
    isRangeActive(range) {
      return (
        this.localFilters.fromDate === range.from &&
        this.localFilters.toDate === range.to
      );
    },
    // Si hay una semana (o el mes) elegida, se mantiene la misma al cambiar de
    // mes: "Week 2" de agosto pasa a "Week 2" de septiembre.
    shiftMonth(delta) {
      const selectedIndex = this.monthRanges.findIndex(this.isRangeActive);
      const [year, month] = this.month.split("-").map(Number);
      this.month = monthKey(new Date(year, month - 1 + delta, 1));
      if (selectedIndex === -1) return;
      const range = this.monthRanges[
        Math.min(selectedIndex, this.monthRanges.length - 1)
      ];
      this.setDates(range.from, range.to);
    },
    apply() {
      if (!this.canApply) return;
      this.$emit("apply");
    },
    clear() {
      this.$emit("clearFilters");
    },
    onKeydown(e) {
      if (e.key !== "Escape" || this.showInfoautoSearch) return;
      // El Esc dentro del buscador de Infoauto (u otro modal) ya cerro ese
      // modal antes de llegar aqui; no debe cerrar tambien el panel.
      if (e.target instanceof Element && e.target.closest(".modal")) return;
      this.$emit("closed");
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

$border: #e5e7eb;
$muted: #6b7280;
$text: #111827;
$accent-soft: rgba(18, 110, 106, 0.08);

// Ojo: el estado elegido usa .is-selected y no .active; _base-modal.vue define
// un .active { display: block !important } global que rompe los flex.

.filters-container {
  position: relative;
  width: 100%;
  padding: 8px 15px 4px;

  &.hidden {
    display: none !important;
  }

  // Movil: hoja inferior sobre un fondo oscuro
  @media (max-width: 767px) {
    position: fixed;
    inset: 0;
    z-index: 50;
    padding: 0;
    background: rgba(17, 24, 39, 0.5);
    display: flex;
    align-items: flex-end;
    animation: fade-in 0.2s ease-out;
  }
}

.filters-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  background: white;
  border: 1px solid $border;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.06);
  animation: panel-in 0.15s ease-out;

  @media (max-width: 767px) {
    max-height: 92vh;
    max-height: 92dvh;
    border: none;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -8px 30px rgba(0, 0, 0, 0.2);
    animation: sheet-up 0.22s ease-out;
  }
}

@keyframes panel-in {
  from {
    transform: translateY(-4px);
    opacity: 0;
  }
  to {
    transform: none;
    opacity: 1;
  }
}

@keyframes sheet-up {
  from {
    transform: translateY(24px);
    opacity: 0;
  }
  to {
    transform: none;
    opacity: 1;
  }
}

@keyframes fade-in {
  from {
    background: rgba(17, 24, 39, 0);
  }
}

.sheet-handle {
  display: none;

  @media (max-width: 767px) {
    display: block;
    width: 40px;
    height: 4px;
    margin: 8px auto 0;
    border-radius: 999px;
    background: #d1d5db;
    flex-shrink: 0;
  }
}

.filters-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 10px 16px;
  border-bottom: 1px solid $border;

  .filters-title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    i {
      font-size: 20px;
      color: $link-color-1;
    }

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: $text;
    }
  }

  .active-count {
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(18, 110, 106, 0.1);
    color: $link-color-1;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
  }
}

.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  color: $muted;
  transition: background 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    color: $text;
  }

  &:focus-visible {
    outline: 2px solid $link-color-1;
    outline-offset: 1px;
  }

  i {
    font-size: 20px;
  }

  &.small {
    width: 32px;
    height: 32px;

    i {
      font-size: 18px;
    }
  }
}

.filters-body {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;

  @media (max-width: 1279px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }
}

.filter-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px 16px;
  min-width: 0;

  & + & {
    border-left: 1px solid $border;
  }

  @media (max-width: 1279px) {
    &:nth-child(3) {
      grid-column: 1 / -1;
      border-left: none;
      border-top: 1px solid $border;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 16px;
      align-content: start;

      .section-head {
        grid-column: 1 / -1;
      }
    }
  }

  @media (max-width: 767px) {
    & + & {
      border-left: none;
      border-top: 1px solid $border;
    }

    &:nth-child(3) {
      display: flex;
    }
  }
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 24px;
}

.section-title {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $muted;
}

.reset-btn {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: $link-color-1;

  &:hover {
    background: $accent-soft;
  }
}

.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.field-label {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.field-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  &.compact {
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 6px;
  }
}

.range-sep {
  color: $muted;
}

.field-input {
  width: 100%;
  min-width: 0;
  height: 38px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: white;
  font-size: 14px;
  color: $text;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder {
    color: #9ca3af;
  }

  &:hover:not(:focus) {
    border-color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: $link-color-1;
    box-shadow: 0 0 0 3px rgba(18, 110, 106, 0.12);
  }

  &.invalid {
    border-color: #dc2626;

    &:focus {
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12);
    }
  }

  // 16px evita el zoom automatico de iOS al enfocar
  @media (max-width: 767px) {
    height: 44px;
    font-size: 16px;
  }
}

.field-error {
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 12px;
  color: #dc2626;
}

.range-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: -4px 0 0;
  font-size: 13px;
  font-weight: 500;
  color: $link-color-1;

  i {
    font-size: 16px;
  }

  &.empty {
    font-weight: 400;
    color: $muted;
  }
}

.month-picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 10px 10px;
  border: 1px solid $border;
  border-radius: 10px;
  background: #f9fafb;
}

.month-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .icon-btn {
    width: 32px;
    height: 32px;

    &:hover:not(:disabled) {
      background: white;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }

  .month-label {
    font-size: 14px;
    font-weight: 600;
    color: $text;
  }
}

.week-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.week-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 6px 4px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  line-height: 1.2;
  transition: background 0.15s, border-color 0.15s;

  &:hover:not(.is-selected) {
    border-color: $link-color-1;
    background: $accent-soft;
  }

  &:focus-visible {
    outline: 2px solid $link-color-1;
    outline-offset: 1px;
  }

  .week-name {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    white-space: nowrap;
  }

  .week-days {
    font-size: 11px;
    color: $muted;
    font-variant-numeric: tabular-nums;
  }

  &.is-selected {
    background: $link-color-1;
    border-color: $link-color-1;

    .week-name,
    .week-days {
      color: white;
    }
  }

  @media (max-width: 767px) {
    padding: 8px 4px;
  }
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chip {
  padding: 5px 10px;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  background: white;
  font-size: 13px;
  color: #374151;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover:not(.is-selected) {
    border-color: $link-color-1;
    background: $accent-soft;
  }

  &:focus-visible {
    outline: 2px solid $link-color-1;
    outline-offset: 1px;
  }

  &.is-selected {
    background: $link-color-1;
    border-color: $link-color-1;
    color: white;
  }

  @media (max-width: 767px) {
    padding: 8px 12px;
  }
}

.segmented {
  display: flex;
  padding: 3px;
  border-radius: 8px;
  background: #f3f4f6;

  button {
    flex: 1;
    min-width: 0;
    height: 32px;
    padding: 0 8px;
    border-radius: 6px;
    font-size: 13px;
    color: #4b5563;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: background 0.15s, color 0.15s, box-shadow 0.15s;

    &:hover:not(.is-selected) {
      color: $text;
      background: rgba(255, 255, 255, 0.6);
    }

    &:focus-visible {
      outline: 2px solid $link-color-1;
      outline-offset: -2px;
    }

    &.is-selected {
      background: white;
      color: $link-color-1;
      font-weight: 600;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
    }

    @media (max-width: 767px) {
      height: 38px;
      font-size: 14px;
    }
  }
}

.input-with-action {
  display: flex;
  gap: 6px;

  .action-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    padding: 0 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    color: $link-color-1;
    transition: background 0.15s, border-color 0.15s;

    &:hover {
      border-color: $link-color-1;
      background: $accent-soft;
    }

    i {
      font-size: 18px;
    }
  }
}

.selected-customer {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 3px 3px 3px 8px;
  border: 1px solid rgba(18, 110, 106, 0.35);
  border-radius: 8px;
  background: $accent-soft;

  > i {
    font-size: 24px;
    color: $link-color-1;
  }

  .selected-customer-text {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    line-height: 1.25;

    .name {
      font-size: 14px;
      font-weight: 600;
      color: $text;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .id {
      font-size: 12px;
      color: $muted;
    }
  }

  .icon-btn:hover {
    background: white;
  }

  @media (max-width: 767px) {
    min-height: 44px;
  }
}

.switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid $border;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f9fafb;
  }
}

.switch {
  appearance: none;
  position: relative;
  flex-shrink: 0;
  width: 38px;
  height: 22px;
  border-radius: 999px;
  background: #d1d5db;
  cursor: pointer;
  transition: background 0.15s;

  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: 3px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    transition: transform 0.15s;
  }

  &:checked {
    background: $link-color-1;

    &::after {
      transform: translateX(16px);
    }
  }

  &:focus-visible {
    outline: 2px solid $link-color-1;
    outline-offset: 2px;
  }
}

.filters-footer {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid $border;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;

  @media (max-width: 767px) {
    border-radius: 0;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));

    button {
      flex: 1;
    }

    .btn-primary {
      flex: 2;
    }
  }
}

.btn-primary,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  transition: background 0.15s, filter 0.15s, opacity 0.15s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid $link-color-1;
    outline-offset: 2px;
  }

  i {
    font-size: 18px;
  }

  @media (max-width: 767px) {
    height: 46px;
    font-size: 15px;
  }
}

.btn-primary {
  background: $link-color-1;
  color: white;

  &:hover:not(:disabled) {
    filter: brightness(1.1);
  }
}

.btn-secondary {
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
}
</style>
